/**
 * Lightweight `sessionStorage` shuttle used to attribute Stripe checkout
 * results to the plan the user originally selected. Successful purchase data
 * is verified directly against Stripe; this browser record supplies canceled
 * checkout attribution and lets the return tracker diagnose a mismatch without
 * making conversion delivery depend on browser storage.
 *
 * Lives outside both the route and the component so we can share the
 * read/write surface without creating an app-route -> component import
 * cycle.
 */

const STORAGE_KEY = 'gtm_pending_checkout_selection'

export type PendingCheckoutSelection = {
  checkoutSessionId: string
  planKey: string
  planName: string
  planPriceCents: number
  planPriceCurrency: string
  priorPlanName: string
}

export function readPendingCheckoutSelection(): PendingCheckoutSelection | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<PendingCheckoutSelection>
    if (
      typeof parsed.checkoutSessionId !== 'string' ||
      typeof parsed.planKey !== 'string' ||
      typeof parsed.planName !== 'string' ||
      typeof parsed.planPriceCents !== 'number' ||
      typeof parsed.planPriceCurrency !== 'string' ||
      typeof parsed.priorPlanName !== 'string'
    ) {
      return null
    }
    return parsed as PendingCheckoutSelection
  } catch {
    return null
  }
}

export function writePendingCheckoutSelection(
  selection: PendingCheckoutSelection
): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selection))
  } catch {
    // sessionStorage may be disabled in private browsing — swallow.
  }
}

export function clearPendingCheckoutSelection(): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
